/**
 * TaskListTabBar — horizontal-scroll list of tabs with an animated
 * underline. Active tab auto-scrolls into view. The right edge fades
 * to hint at horizontal overflow.
 *
 * The "+" add-list button and the right accessory (filter) live to the
 * right of the scroll region, outside the fade.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { TaskList } from '@/types/task-lists';
import { TaskListAddButton } from './TaskListAddButton';

const UNDERLINE_HEIGHT = 2.5;
const ANIMATION_DURATION = 200;
const FADE_WIDTH = 28;
const SCROLL_PADDING = 24;

type TaskListTabBarProps = {
  lists: TaskList[];
  activeListId: string | null;
  currentUserId: string | null;
  atCap: boolean;
  onSelectList: (listId: string) => void;
  onRequestCreateList: () => void;
  rightAccessory?: React.ReactNode;
};

export function TaskListTabBar({
  lists,
  activeListId,
  currentUserId,
  atCap,
  onSelectList,
  onRequestCreateList,
  rightAccessory,
}: TaskListTabBarProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const scrollRef = useRef<ScrollView>(null);
  const tabWidths = useRef<Map<string, number>>(new Map());
  const tabOffsets = useRef<Map<string, number>>(new Map());
  const scrollViewWidth = useRef(0);
  const underlineX = useSharedValue(0);
  const underlineWidth = useSharedValue(0);

  const [contentWidth, setContentWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const atEnd = viewportWidth > 0 && scrollX + viewportWidth >= contentWidth - 1;
  const atStart = scrollX <= 1;

  const updateUnderline = useCallback(
    (key: string) => {
      const x = tabOffsets.current.get(key) ?? 0;
      const w = tabWidths.current.get(key) ?? 0;
      underlineX.value = withTiming(x, { duration: ANIMATION_DURATION });
      underlineWidth.value = withTiming(w, { duration: ANIMATION_DURATION });
    },
    [underlineX, underlineWidth],
  );

  const ensureVisible = useCallback((key: string) => {
    const x = tabOffsets.current.get(key);
    const w = tabWidths.current.get(key);
    const viewport = scrollViewWidth.current;
    if (x === undefined || w === undefined || viewport === 0) return;
    // Naive scroll: position the active tab's left edge with a small inset.
    const target = Math.max(0, x - SCROLL_PADDING);
    scrollRef.current?.scrollTo({ x: target, animated: true });
    void w;
    void viewport;
  }, []);

  useEffect(() => {
    if (!activeListId) return;
    updateUnderline(activeListId);
    ensureVisible(activeListId);
  }, [activeListId, updateUnderline, ensureVisible]);

  const handleTabLayout = useCallback(
    (key: string, event: LayoutChangeEvent) => {
      const { x, width } = event.nativeEvent.layout;
      tabWidths.current.set(key, width);
      tabOffsets.current.set(key, x);
      if (key === activeListId) updateUnderline(key);
    },
    [activeListId, updateUnderline],
  );

  const underlineStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: underlineX.value }],
    width: underlineWidth.value,
  }));

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <View
          style={styles.scrollWrapper}
          onLayout={e => {
            scrollViewWidth.current = e.nativeEvent.layout.width;
            setViewportWidth(e.nativeEvent.layout.width);
          }}
        >
          <ScrollView
            ref={scrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            bounces={false}
            overScrollMode="never"
            scrollEventThrottle={16}
            onScroll={e => setScrollX(e.nativeEvent.contentOffset.x)}
            onContentSizeChange={(width) => setContentWidth(width)}
          >
            <View>
              <View style={styles.tabsRow}>
                {lists.map(list => {
                  const isActive = list.id === activeListId;
                  const isShared =
                    list.ownerUserId !== currentUserId || list.participantUserIds.length > 0;
                  return (
                    <Pressable
                      key={list.id}
                      onLayout={e => handleTabLayout(list.id, e)}
                      onPress={() => onSelectList(list.id)}
                      style={styles.tab}
                    >
                      <View style={styles.tabInner}>
                        <Text
                          style={[
                            styles.tabLabel,
                            {
                              fontFamily: getBodyFont(),
                              color: isActive ? palette.text : palette.textMuted,
                              fontWeight: isActive ? '600' : '400',
                            },
                          ]}
                          numberOfLines={1}
                        >
                          {list.name}
                        </Text>
                        {isShared && (
                          <Ionicons
                            name="people-outline"
                            size={13}
                            color={palette.textMuted}
                          />
                        )}
                      </View>
                    </Pressable>
                  );
                })}
              </View>
              <View
                style={[
                  styles.track,
                  {
                    backgroundColor: palette.borderMuted,
                    width: Math.max(contentWidth, 0),
                  },
                ]}
              >
                <Animated.View
                  style={[
                    styles.underline,
                    { backgroundColor: palette.tint },
                    underlineStyle,
                  ]}
                />
              </View>
            </View>
          </ScrollView>
          {!atStart && (
            <LinearGradient
              colors={[palette.background, `${palette.background}00`]}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
              pointerEvents="none"
              style={styles.fadeLeft}
            />
          )}
          {!atEnd && (
            <LinearGradient
              colors={[`${palette.background}00`, palette.background]}
              start={{ x: 0, y: 0.5 }}
              end={{ x: 1, y: 0.5 }}
              pointerEvents="none"
              style={styles.fadeRight}
            />
          )}
        </View>

        <View style={styles.rightAccessory}>
          <TaskListAddButton disabled={atCap} onPress={onRequestCreateList} />
          {rightAccessory}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { paddingLeft: Spacing.lg, paddingTop: Spacing.sm },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  scrollWrapper: { flex: 1, position: 'relative' },
  tabsRow: { flexDirection: 'row', gap: Spacing.xl },
  tab: { paddingVertical: Spacing.sm },
  tabInner: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  tabLabel: { fontSize: 15 },
  track: { height: 1, position: 'relative' },
  underline: {
    position: 'absolute',
    bottom: 0,
    height: UNDERLINE_HEIGHT,
    borderRadius: UNDERLINE_HEIGHT / 2,
  },
  fadeRight: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: FADE_WIDTH,
  },
  fadeLeft: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: FADE_WIDTH,
  },
  rightAccessory: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    paddingLeft: Spacing.xs,
    paddingRight: Spacing.lg,
  },
});
